import React, { useCallback, useEffect, useState } from 'react';
import Header from 'components/header/Header';
import { IStyle } from 'Styles';
import DewarContent from './DewarContent';
import PuckContent from './PuckContent';
import CrystalContainerService from './CrystalContainerService';
import { DewarDTO, Puckv4, SaveDewarDTO, SavePuckDTO } from 'business/models/Models';
import { showSnackBar } from 'utils/Global';
import { RowExtraInfo, RowInfoType } from 'components/customComponents/Controls/RMTable';
import { useModalHandlers } from 'components/modal';
import { KeyboardKeys } from 'constants/';
import { useSaveDiscardButtons } from 'CustomHooks/useSaveDiscardButtons';

const CrystalContainerDashboard = () => {
  const crystalContainerService = new CrystalContainerService();
  const [dewarInfo, setDewarInfo] = useState([]);
  const [puckInfo, setPuckInfo] = useState([]);
  const [initialDewarInfo, setInitialDewarInfo] = useState([]);
  const [initialPuckInfo, setInitialPuckInfo] = useState([]);
  const [deletedDewars, setDeletedDewars] = useState<number[]>([]);
  const [deletedPucks, setDeletedPucks] = useState<number[]>([]);
  const [dewarRowError, setDewarRowError] = useState({});
  const [puckRowError, setPuckRowError] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const { showModal, hideModal } = useModalHandlers();

  const onSave = () => {
    const dewarRowInfo: RowExtraInfo = {};
    dewarInfo.forEach((dewar, i) => {
      let msg = '';
      if (!dewar.name) {
        msg = 'Name is a required field.\n';
      } else if (!dewar.puckCount) {
        msg = 'Puck Count is a required field.\n';
      } else if (
        dewar.name &&
        dewarInfo.find((x) => x.name === dewar.name && x.index !== i)
      ) {
        msg = 'name should be unique.\n';
      }
      if (msg) {
        dewarRowInfo[i] = { msg, type: RowInfoType.Error };
      }
    });
    const puckRowInfo: RowExtraInfo = {};
    puckInfo.forEach((puck, i) => {
      let msg = '';
      if (!puck.name) {
        msg = 'Name is a required field.\n';
      } else if (!puck.barcode) {
        msg = 'Barcode is a required field.\n';
      } else if (
        puck.name &&
        puckInfo.find((x) => x.name === puck.name && x.index !== i)
      ) {
        msg = 'name should be unique.\n';
      } else if (
        puck.name &&
        puckInfo.find((x) => x.barcode === puck.barcode && x.index !== i)
      ) {
        msg = 'barcode should be unique.\n';
      }
      if (msg) {
        puckRowInfo[i] = { msg, type: RowInfoType.Error };
      }
    });
    if (Object.keys(dewarRowInfo).length) {
      setDewarRowError(dewarRowInfo);
    } else {
      setDewarRowError({});
    }
    if (Object.keys(puckRowInfo).length) {
      setPuckRowError(puckRowInfo);
    } else {
      setPuckRowError({});
    }
    if (!Object.keys(puckRowInfo).length && !Object.keys(dewarRowInfo).length) {
      const dewarsData: DewarDTO[] = dewarInfo.filter((x) => x.rowIsDirty);
      const dewarDtoList: DewarDTO[] = [];
      dewarsData.forEach((item) => {
        const dewarDto = new DewarDTO();
        if (item.id) {
          dewarDto.id = item.id;
          dewarDto.name = item.name;
          dewarDto.puckCount = item.puckCount;
          dewarDto.enable = item.enable;
        } else {
          dewarDto.name = item.name;
          dewarDto.puckCount = item.puckCount;
          dewarDto.enable = item.enable;
        }
        dewarDtoList.push(dewarDto);
      });

      const saveDewars = new SaveDewarDTO();
      saveDewars.dewarDtoList = dewarDtoList as DewarDTO[];
      saveDewars.deletedIds = deletedDewars as number[];
      crystalContainerService.saveDewars(saveDewars).then((response) => {
        const newDewarInfoList = [...dewarInfo].map((item) => ({
          ...item,
          id: item.id ? item.id : response.find((x) => x.name === item.name)?.id,
          rowIsDirty: false,
        }));
        setDewarInfo(newDewarInfoList);
      });

      const pucksData: Puckv4[] = puckInfo.filter((x) => x.rowIsDirty);
      const puckDtoList: Puckv4[] = [];
      pucksData.forEach((item) => {
        const puckDto = new Puckv4();
        if (item.id) {
          puckDto.id = item.id;
          puckDto.name = item.name;
          puckDto.barcode = item.barcode;
          puckDto.puckTypeId = item.puckTypeId;
          puckDto.enable = item.enable;
        } else {
          puckDto.name = item.name;
          puckDto.barcode = item.barcode;
          puckDto.puckTypeId = item.puckTypeId;
          puckDto.enable = item.enable;
        }
        puckDtoList.push(puckDto);
      });

      const savePucks = new SavePuckDTO();
      savePucks.puckDtoList = puckDtoList;
      savePucks.deletedIds = deletedPucks as number[];
      crystalContainerService.savePucks(savePucks).then((response) => {
        const newPuckInfoList = [...puckInfo].map((item) => ({
          ...item,
          id: item.id ? item.id : response.find((x) => x.name === item.name)?.id,
          rowIsDirty: false,
        }));
        setPuckInfo(newPuckInfoList);
      });
      setDeletedDewars([]);
      setDeletedPucks([]);
      setInitialDewarInfo(dewarInfo);
      setInitialPuckInfo(puckInfo);
      setIsDirty(false);
      showSnackBar('Changes have been saved');
    }
  };

  const onDiscard = () => {
    setDewarInfo(initialDewarInfo);
    setPuckInfo(initialPuckInfo);
    setIsDirty(false);
    setDeletedDewars([]);
    setDeletedPucks([]);
    setDewarRowError({});
    setPuckRowError({});
  };

  const saveDiscardButtons = useSaveDiscardButtons({
    enableButtons: isDirty,
    onSave: onSave,
    onDiscard: onDiscard,
  });

  const inUseAlert = (type: string) => {
    showModal({
      body: (
        <>
          <div id="inusealert-body-text">
            You cannot edit or delete this {type} because it is used in a Crystal
            definition.
          </div>
        </>
      ),
      heading: `Edit ${type}`,
      onOk: hideModal,
      isOkOnly: true,
      okButtonText: 'OK',
    });
  };
  const handleUserKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const ctrlKey = e.ctrlKey || e.metaKey;

      if (ctrlKey && e.key === KeyboardKeys.S) {
        e.preventDefault();
        if (isDirty) {
          onSave();
        }
      }
    },
    [isDirty, onSave],
  );

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    window.addEventListener('keydown', handleUserKeyPress, { signal });

    return () => {
      abortController.abort();
    };
  }, [handleUserKeyPress]);

  return (
    <div id="index-crystalcontainerdashboard-main-container" className="w-full h-full">
      <div id="index-crystalcontainerdashboard-header" className="setup-bg-topbar">
        <Header
          containerStyle={styles.header}
          headerButtons={saveDiscardButtons}
          isNewTheme={true}
          title={'Crystal Container'}
        />
      </div>
      <div
        id="index-crystalcontainerdashboard-content-wrapper"
        className="d-flex flex-row float-left bg-white w-full h-full"
      >
        <div
          id="index-crystalcontainerdashboard-content-container"
          className="flex w-full"
        >
          <DewarContent
            dewarInfo={dewarInfo}
            setDewarInfo={setDewarInfo}
            setInitialDewarInfo={setInitialDewarInfo}
            setIsDirty={setIsDirty}
            deletedDewars={deletedDewars}
            rowExtraInfo={dewarRowError}
            setRowExtraInfo={setDewarRowError}
            inUseAlert={inUseAlert}
          />
          <PuckContent
            puckInfo={puckInfo}
            setPuckInfo={setPuckInfo}
            setInitialPuckInfo={setInitialPuckInfo}
            setIsDirty={setIsDirty}
            deletedPucks={deletedPucks}
            rowExtraInfo={puckRowError}
            setRowExtraInfo={setPuckRowError}
            inUseAlert={inUseAlert}
          />
        </div>
      </div>
    </div>
  );
};

const styles: IStyle = {
  header: {
    marginTop: -6,
  },
  inUseLabel: {
    fontFamily: 'Arial',
    fontWeight: 400,
    marginLeft: '0.75rem',
  },
};

export default CrystalContainerDashboard;
